import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SocialMediaTypeAttributes } from '@albatrosdeveloper/ave-models-package';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavContainer } from '@angular/material/sidenav';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTableModule, MatRowDef } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ICreateSocialMediaType } from './interfaces/create-social-media-type.interface';
import { IUpdateSocialMediaType } from './interfaces/update-social-media-type.interface';
import { SocialMediaTypeService } from './social-media-type.service.service';
import { CustomMatPaginatorIntl } from 'app/mock-api/common/customPaginatorIntl';
import { getCode } from 'app/utils/method';
import { SearchService } from 'app/layout/common/search-page/search-page.service';
import { filter, Subscription } from 'rxjs';
import { FilterComponent } from 'app/common/components/filter/filter.component';
import { FilterSectionComponent } from 'app/common/components/filter-section/filter-section.component';
import { StatusFiltersComponent } from '../../filters/status-filters/status-filters.component';
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";


@Component({
  selector: 'app-social-media-type',
  templateUrl: './social-media-type.component.html',
  styleUrls: ['./social-media-type.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatDialogModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSortModule,
    MatPaginatorModule,
    MatIcon,
    MatSlideToggle,
    MatSelectModule,
    MatOptionModule,
    MatTooltipModule,
    MatExpansionModule,
    MatMenuModule,
    MatButtonModule,
    MatIconModule,
    MatRowDef,
    MatSidenavContainer,
    FormsModule,
    FilterComponent,
    FilterSectionComponent,
    StatusFiltersComponent,
    HoverMenuDirective
  ],
  providers: [{ provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl }],
})
export class SocialMediaTypeComponent implements AfterViewInit, OnDestroy {
  getCode = getCode;
  displayedColumns: string[] = ['nombre', 'logo', 'estado', 'acciones'];
  currentCreateSocialMediaType: ICreateSocialMediaType = this.initializeCreateSocialMediaType();
  currentUpdateSocialMediaType: SocialMediaTypeAttributes | undefined;

  dataSource: SocialMediaTypeAttributes[] = [];
  searchQuery: string = '';
  totalItems: number = 0;
  pageSize: number = 50;
  pageIndex: number = 0;

  dialogRefAdd: MatDialogRef<any>;
  dialogRefEdit: MatDialogRef<any>;

  @ViewChild('editModal') editModal!: TemplateRef<any>;
  @ViewChild('addModal') addModal!: TemplateRef<any>;
  @ViewChild('deleteDialog') deleteDialog!: TemplateRef<any>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('searchInput') searchInput!: ElementRef;

  //BUSCADOR
  searchSubscription: Subscription;

  // Status Filter Properties
  selectedStatuses: number[] = [1, 0]; // Default to show both active and inactive

  constructor(
    private readonly socialMediaTypeService: SocialMediaTypeService,
    private readonly searchService: SearchService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    this.onHandleSearch();
    this.setupPaginationAndSorting();
    this.findAllWithCount();
  }

  private initializeCreateSocialMediaType(): ICreateSocialMediaType {
    return {
      name: '',
      logo: {
        type: '',
        name: '',
        link: '',
        aspectRatio: '',
        mobileName: '',
        mobileLink: '',
        aspectRatioMobile: '',
      },
    };
  }

  private setupPaginationAndSorting(): void {
    this.paginator.page.subscribe(() => this.findAllWithCount());
    this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = 0;
      this.findAllWithCount();
    });
  }

  findAllWithCount(): void {
    const pageIndex = this.paginator.pageIndex + 1;
    const sortField = this.getSortField();
    const sortDirection: 'ASC' | 'DESC' = this.sort.direction === 'asc' ? 'ASC' : 'DESC';

    this.socialMediaTypeService
      .findAllWithCount({
        currentPage: pageIndex,
        limitPerPage: this.pageSize,
        search: this.searchQuery,
        sort: sortField,
        sortDirection: sortDirection,
        status: this.selectedStatuses.length ? this.selectedStatuses : null,
      })
      .subscribe(data => {
        this.dataSource = data?.data?.[0]?.data ?? [];
        this.totalItems = data?.data?.[0]?.pageInfo?.[0]?.total ?? 0;
        this.paginator.length = this.totalItems;
      });
  }

  private getSortField(): keyof SocialMediaTypeAttributes {
    const validSortFields: (keyof SocialMediaTypeAttributes)[] = ['name', 'logo', 'statusId'];
    return validSortFields.includes(this.sort.active as keyof SocialMediaTypeAttributes)
      ? (this.sort.active as keyof SocialMediaTypeAttributes)
      : 'name';
  }

  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.findAllWithCount();
  }

  onSearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery = input.value.trim();
    if (this.searchQuery.length >= 2 || this.searchQuery.length === 0) {
      this.paginator.pageIndex = 0;
      this.findAllWithCount();
    }
  }

  public onHandleSearch() {
    this.searchSubscription = this.searchService.searchTerm$.subscribe(term => {
      this.searchQuery = term;
      if (this.searchQuery.length >= 2 || this.searchQuery.length === 0) {
        this.paginator.pageIndex = 0;
        this.findAllWithCount();
      }
    });
  }

  trackByFn(index: number, item: SocialMediaTypeAttributes): string {
    return item._id.toString();
  }

  openCreateModal(): void {
    this.currentCreateSocialMediaType = this.initializeCreateSocialMediaType();
    this.dialogRefAdd = this.dialog.open(this.addModal, {
      panelClass: 'form-modal',
      disableClose: true,
    });
    // this.dialogRefAdd.afterClosed().subscribe((result) => result && this.createSocialMediaType());
    this.dialogRefAdd.afterClosed().subscribe(result => {
      result;
    });
  }

  openEditModal(socialMediaType: SocialMediaTypeAttributes): void {
    this.currentUpdateSocialMediaType = { ...socialMediaType };
    this.dialogRefEdit = this.dialog.open(this.editModal, {
      panelClass: 'form-modal',
      disableClose: true,
    });
    // this.dialogRefEdit.afterClosed().subscribe((result) => result && this.updateSocialMediaType(socialMediaType._id.toString()));
    this.dialogRefEdit.afterClosed().subscribe(result => {
      result;
    });
  }

  createSocialMediaType(): void {
    this.socialMediaTypeService.create(this.currentCreateSocialMediaType).subscribe({
      next: () => {
        this.snackBar.open('Registro creado con éxito', 'Cerrar', { duration: 3000 });
        this.findAllWithCount();
        this.dialogRefAdd.close();
      },
      error: err => {
        this.snackBar.open(err.error.message || 'Error al crear el registro', 'Cerrar', {
          duration: 3000,
        });
      },
    });
  }

  updateSocialMediaType(id: string): void {
    if (this.currentUpdateSocialMediaType) {
      const updatedSocialMediaType: IUpdateSocialMediaType = {
        ...this.currentUpdateSocialMediaType,
      };

      this.socialMediaTypeService.update(id, updatedSocialMediaType).subscribe({
        next: () => {
          this.snackBar.open('Registro actualizado con éxito', 'Cerrar', { duration: 3000 });
          this.findAllWithCount();
          this.dialogRefEdit.close();
        },
        error: err => {
          this.snackBar.open(err.error.message || 'Error al actualizar el registro', 'Cerrar', {
            duration: 3000,
          });
        },
      });
    }
  }

  onStatusChange(event: boolean, socialMediaType: SocialMediaTypeAttributes): void {
    const originalStatusId = socialMediaType.statusId;
    const updatedStatusId = event ? 1 : 0;
    const updatedSocialMediaType = { ...socialMediaType, statusId: updatedStatusId };

    this.socialMediaTypeService
      .update(socialMediaType._id.toString(), updatedSocialMediaType)
      .subscribe({
        next: () => {
          this.findAllWithCount();
        },
        error: () => {
          socialMediaType.statusId = originalStatusId; // Volver al estado original si falla
          this.snackBar.open('Error al actualizar el estado.', 'Cerrar', { duration: 3000 });
          this.findAllWithCount();
        },
      });
  }

  openDeleteDialog(socialMediaType: SocialMediaTypeAttributes): void {
    this.dialog
      .open(this.deleteDialog, {
        data: socialMediaType,
        panelClass: 'delete-modal',
        disableClose: true,
      })
      .afterClosed()
      .subscribe(result => result && this.delete(socialMediaType._id.toString()));
  }

  delete(id: string): void {
    this.socialMediaTypeService.remove(id).subscribe(() => {
      this.findAllWithCount();
      this.snackBar.open('Registro eliminado con éxito', 'Cerrar', { duration: 3000 });
    });
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }

  // Filter Methods
  getList(): void {
    this.findAllWithCount();
  }

  clearFilters(): void {
    this.selectedStatuses = [1, 0]; // Reset to default (show both active and inactive)
    this.findAllWithCount();
  }

  applyStatusFilter(): void {
    this.paginator.pageIndex = 0; // Reset to first page when filtering
    this.findAllWithCount();
  }

  onFilterEvent(event: any): void {
    this.getList();
  }

  onCleanEvent(event: any): void {
    this.clearFilters();
  }
}
